import { useState } from 'react';
import { FileText, Upload } from 'lucide-react';
import { DeleteDocumentDialog, documentColumns } from '../components/columns';
import { DocumentEditModal, DocumentUploadModal } from '../components/forms/DocumentModals';
import { CarSelect } from '../components/forms/EntitySelects';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable } from '../components/ui/DataTable';
import { FilterBar, SearchInput } from '../components/ui/FilterBar';
import { Select } from '../components/ui/Input';
import { useDeleteDocument, useDocuments } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import type { Document } from '../types';
import { DOC_TYPE_LABELS, toOptions } from '../utils/helpers';
import { t } from '../i18n';

export function Documents() {
  const [filters, setFilters] = useUrlFilters({ search: '', car_id: '', doc_type: '', validity: '', page: '1', page_size: '20' });
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState<Document | null>(null);
  const remove = useDeleteDocument();

  const page = toInt(filters.page, 1)!;
  const pageSize = toInt(filters.page_size, 20)!;
  const { data, isLoading, isFetching } = useDocuments({
    search: filters.search,
    car_id: toInt(filters.car_id),
    doc_type: filters.doc_type,
    expiring_soon: filters.validity === 'expiring' || filters.validity === 'soon' || undefined,
    expired: filters.validity === 'expiring' || filters.validity === 'expired' || undefined,
    page,
    page_size: pageSize,
  });

  return (
    <>
      <PageHeader
        title={t('Dokumenty')}
        subtitle={t('Poistky, technické preukazy, protokoly a ďalšie súbory k vozidlám')}
        actions={
          <Button onClick={() => setUploading(true)}>
            <Upload className="h-4 w-4" />  {t('Nahrať dokumenty')}
          </Button>
        }
      />
      <Card padding="none">
        <FilterBar>
          <SearchInput value={filters.search} onChange={(search) => setFilters({ search })} placeholder={t('Názov, súbor, poznámka…')} />
          <CarSelect label={t('Vozidlo')} wrapperClassName="sm:w-64" placeholder={t('Všetky vozidlá')} value={filters.car_id} onChange={(e) => setFilters({ car_id: e.target.value })} />
          <Select
            label={t('Typ')}
            wrapperClassName="sm:w-48"
            placeholder={t('Všetky typy')}
            options={toOptions(DOC_TYPE_LABELS)}
            value={filters.doc_type}
            onChange={(e) => setFilters({ doc_type: e.target.value })}
          />
          <Select
            label={t('Platnosť')}
            wrapperClassName="sm:w-56"
            placeholder={t('Ľubovoľná')}
            options={[
              { value: 'expiring', label: t('Neplatné alebo končia do 30 dní') },
              { value: 'soon', label: t('Končia do 30 dní') },
              { value: 'expired', label: t('Neplatné') },
            ]}
            value={filters.validity}
            onChange={(e) => setFilters({ validity: e.target.value })}
          />
        </FilterBar>
        <DataTable
          data={data?.documents}
          columns={documentColumns({ onEdit: setEditing, onDelete: setDeleting, showCar: true })}
          rowKey={(d) => d.id}
          isLoading={isLoading}
          isFetching={isFetching}
          emptyMessage={<EmptyState icon={<FileText className="h-10 w-10" />} title={t('Žiadne dokumenty nezodpovedajú filtru')} />}
          pagination={
            data && {
              page,
              pageSize,
              total: data.total,
              onPageChange: (p) => setFilters({ page: String(p) }),
              onPageSizeChange: (s) => setFilters({ page_size: String(s) }),
            }
          }
        />
      </Card>
      <DocumentUploadModal isOpen={uploading} onClose={() => setUploading(false)} carId={toInt(filters.car_id)} />
      <DocumentEditModal document={editing} onClose={() => setEditing(null)} />
      <DeleteDocumentDialog document={deleting} onClose={() => setDeleting(null)} remove={remove} />
    </>
  );
}
